import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { IconLock, IconBackspace, IconLoader, IconX } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { http, getAuthorizationHeader } from '../../config/http';

export default function PinBrankas({
  onSuccess,
  maxRetries,
  onFailure,
  onCancel,
  title = 'Buka Brankas',
  subtitle = 'Masukkan 6 digit PIN keamanan untuk mengakses Brankas Pusat.'
}) {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [correctPin, setCorrectPin] = useState('123456'); // Fallback
  const [correctOwnerPin, setCorrectOwnerPin] = useState('123456');
  const [retryCount, setRetryCount] = useState(0);
  const MAX_LENGTH = 6;
  
  useEffect(() => {
    async function fetchPin() {
      try {
        const res = await http.get('/settings', { headers: { Authorization: getAuthorizationHeader() } });
        const data = res.data?.data || {};
        const role = (localStorage.getItem('role') || '').toLowerCase().trim();
        const toko = (localStorage.getItem('toko') || '').toLowerCase();
        const cabangId = localStorage.getItem('cabang_id');
        
        const ownerPin = data.owner_pin || data.pin_master || '123456';
        setCorrectOwnerPin(ownerPin);

        let targetPin = ownerPin;
        if (role === 'admin' || role === 'owner') {
          targetPin = ownerPin;
        } else {
          const storePins = data.store_pins || {};
          const key = 'pin_toko_' + toko.replace(/[^a-z0-9]/g, '');

          if (cabangId && storePins[cabangId]) {
            targetPin = String(storePins[cabangId]);
          } else if (data[key]) {
            targetPin = String(data[key]);
          } else {
            targetPin = ownerPin;
          }
        }
        setCorrectPin(targetPin);
      } catch (error) {
        console.error("Failed to fetch PIN settings:", error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchPin();
  }, []);

  useEffect(() => {
    if (pin.length === MAX_LENGTH) {
      handleVerify();
    }
  }, [pin]);

  const handleKeyPress = (num) => {
    if (pin.length < MAX_LENGTH) {
      setPin(prev => prev + num);
      setIsError(false);
    }
  };

  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setIsError(false);
  };

  const handleVerify = () => {
    // Valid jika cocok dengan PIN toko akun tersebut atau PIN Owner master
    const isValid = pin === correctPin || (correctOwnerPin && pin === correctOwnerPin);
    if (isValid) {
      toast.success('PIN Berhasil Diverifikasi!', { autoClose: 1000 });
      setTimeout(() => {
        onSuccess();
      }, 300);
    } else {
      setIsError(true);
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);
      
      if (maxRetries && newRetryCount >= maxRetries) {
        toast.error(`PIN Salah ${newRetryCount}x! Proses dibatalkan.`, { autoClose: 2000 });
        if (onFailure) {
          setTimeout(() => {
            onFailure();
          }, 1000);
        }
      } else {
        toast.error('PIN Salah! Silakan coba lagi.', { autoClose: 1500 });
        setTimeout(() => {
          setPin('');
          setIsError(false);
        }, 500);
      }
    }
  };

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (/^[0-9]$/.test(e.key)) {
        handleKeyPress(e.key);
      } else if (e.key === 'Backspace') {
        handleDelete();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pin]);

  const modalContent = (
    <div className="fixed inset-0 z-[9999999] bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className={`relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-8 border border-slate-200 dark:border-slate-800 flex flex-col items-center transform transition-transform ${isError ? 'animate-[shake_0.4s_ease-in-out]' : ''}`}>
        
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="absolute top-5 right-5 p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <IconX size={20} />
          </button>
        )}

        <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30 mb-6">
          <IconLock size={32} className="text-white" />
        </div>
        
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2 text-center">{title}</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-8">
          {subtitle}
        </p>

        {/* PIN Dots */}
        <div className="flex gap-4 mb-10">
          {[...Array(MAX_LENGTH)].map((_, i) => (
            <div 
              key={i} 
              className={`w-4 h-4 rounded-full transition-all duration-300 ${
                i < pin.length 
                  ? 'bg-amber-500 scale-110 shadow-[0_0_10px_rgba(245,158,11,0.5)]' 
                  : 'bg-slate-200 dark:bg-slate-700'
              } ${isError ? 'bg-red-500' : ''}`}
            />
          ))}
        </div>

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-4 w-full px-4 relative">
          {isLoading && (
            <div className="absolute inset-0 z-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center rounded-2xl">
               <IconLoader className="animate-spin text-amber-500" size={32} />
            </div>
          )}
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              disabled={isLoading}
              onClick={() => handleKeyPress(num.toString())}
              className="h-14 rounded-2xl bg-slate-50 dark:bg-slate-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-xl font-bold text-slate-700 dark:text-slate-200 hover:text-amber-600 dark:hover:text-amber-400 transition-all active:scale-95 shadow-sm border border-slate-100 dark:border-slate-700/50"
            >
              {num}
            </button>
          ))}
          <div className="col-start-1"></div>
          <button
            onClick={() => handleKeyPress('0')}
            disabled={isLoading}
            className="h-14 rounded-2xl bg-slate-50 dark:bg-slate-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-xl font-bold text-slate-700 dark:text-slate-200 hover:text-amber-600 dark:hover:text-amber-400 transition-all active:scale-95 shadow-sm border border-slate-100 dark:border-slate-700/50"
          >
            0
          </button>
          <button
            onClick={handleDelete}
            disabled={pin.length === 0 || isLoading}
            className="h-14 rounded-2xl bg-slate-50 dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-xl font-bold text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 transition-all active:scale-95 shadow-sm border border-slate-100 dark:border-slate-700/50 flex items-center justify-center disabled:opacity-50 disabled:active:scale-100 disabled:hover:bg-slate-50 dark:disabled:hover:bg-slate-800"
          >
            <IconBackspace size={24} />
          </button>
        </div>

        <button
          type="button"
          onClick={() => {
            if (onCancel) {
              onCancel();
            } else {
              navigate('/');
            }
          }}
          className="mt-6 px-4 py-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
          {onCancel ? 'Batal' : 'Kembali ke Dashboard'}
        </button>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `}} />
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }
  return modalContent;
}
